/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react'
import inputs from 'src/components/inputs'
import { useTranslate } from 'ra-core'
import sanitizeFormProps from './sanitizeRestProps'
import { IInputProps } from 'src/types'
import { getPropUntyped } from '@obvious.tech/util/record'
import { useAppSelector } from 'src/redux/createAdminStore'
import { selectDefinitions } from 'src/redux/definitions/definitions.selectors'
import { selectIsEntityMicroserviceAvailable } from 'src/redux/api/api.selectors'
import { getOverridenReferencedProperty } from 'src/parser/EntitiesCustomization'

const GenericInput = ({
  property,
  name,
  onChange,
  value,
  ...rest
}: IInputProps<any>): JSX.Element => {
  const translate = useTranslate()
  const definitions = useAppSelector(selectDefinitions)
  const isRelatedMicroserviceAvailable = useAppSelector(
    selectIsEntityMicroserviceAvailable(property.reference?.target),
  )
  const { enums = {}, objects = {} } = definitions
  const { type, $ref, id, displayedName } = property

  const inputProps = {
    ...sanitizeFormProps(rest),
    name,
    value,
    onChange,
    property: {
      ...property,
      displayedName: translate(displayedName ?? name ?? id ?? ''),
    },
  }

  if (typeof property.basedOn === 'string') {
    return <inputs.basedOn {...inputProps} />
  }
  if (property.displayedComponent !== undefined) {
    return <property.displayedComponent {...inputProps} />
  }
  if (
    typeof property.reference === 'object' &&
    property.reference !== null &&
    isRelatedMicroserviceAvailable
  ) {
    const componentPath = `reference.${property.type ?? 'undefined'}`
    const Component = getPropUntyped(inputs, componentPath).unwrapOr(null)
    if (Component == null) throw new Error(`No such input: ${componentPath}.`)

    // @ts-expect-error
    // eslint-disable-next-line react-hooks/static-components
    return <Component {...inputProps} />
  }
  if (typeof $ref === 'string') {
    const reference = $ref.split('/')[2]
    if (typeof type !== 'string' || !(type in inputs)) {
      if (reference in objects) {
        return (
          <inputs.object
            {...inputProps}
            property={getOverridenReferencedProperty(
              objects[reference],
              inputProps.property,
            )}
          />
        )
      }
      if (reference in enums) {
        return (
          <inputs.enum
            {...inputProps}
            // @ts-expect-error
            property={enums[reference]}
          />
        )
      }
    }
  }
  if (typeof type === 'string' && type in inputs) {
    const InputOfType = inputs[type]
    return <InputOfType {...inputProps} />
  }

  console.log(`property ${name} is not defined`)
  return <span key={name} />
}

export default GenericInput
